import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FeaturedStory from './components/FeaturedStory';
import FilterBar from './components/FilterBar';
import StoryCard from './components/StoryCard';
import StatsOverview from './components/StatsOverview';
import TestimonialSection from './components/TestimonialSection';
import CTASection from './components/CTASection';
import Footer from '../../components/ui/Footer';
import { useWebsiteData } from '../../context/WebsiteDataContext';

const SuccessStoriesPage = () => {
  const { getActiveProjects, getPublishedTestimonials } = useWebsiteData();
  const projects = getActiveProjects();
  const testimonials = getPublishedTestimonials();

  const [filters, setFilters] = useState({
    projectType: 'all',
    budgetRange: 'all',
    serviceCategory: 'all',
    sortBy: 'recent'
  });
  const [selectedStory, setSelectedStory] = useState(null);
  const [filteredStories, setFilteredStories] = useState([]);

  // Map projects to success stories format
  const successStories = projects.map(p => ({
    id: p.id,
    title: p.title,
    description: p.description,
    projectType: p.category.charAt(0).toUpperCase() + p.category.slice(1),
    rating: p.rating || 5,
    timeReduction: p.duration || 'N/A',
    costSavings: '10%+', // Default since not in project model
    hasVideo: false,
    heroImage: p.image || '/assets/images/no_image.png',
    client: {
      name: p.client || 'Valued Client',
      location: p.location || 'Pune',
      avatar: 'https://randomuser.me/api/portraits/lego/1.jpg'
    },
    results: {
      timeline: p.duration || 'N/A',
      budget: p.budget || 'N/A'
    },
    servicesUsed: p.services || []
  }));

  const featuredStory = successStories.find(s => s.rating === 5) || successStories[0];

  const statsData = {
    totalProjects: `${projects.length}+` || '500+',
    avgTimeSaved: '8 months',
    totalSavings: '50Cr+',
    avgRating: '4.9/5',
    repeatClients: 85,
    approvalRate: 98,
    avgProjectSize: '₹1.2Cr'
  };

  // Map Firestore testimonials to TestimonialSection format
  const mappedTestimonials = testimonials.map(t => ({
    content: t.content,
    rating: t.rating || 5,
    hasVideo: false,
    client: {
      name: t.name,
      title: t.designation || 'Client',
      location: 'Pune, Maharashtra',
      avatar: t.image || 'https://randomuser.me/api/portraits/lego/1.jpg'
    },
    projectDetails: {
      type: 'Construction',
      timeline: 'On Time',
      budget: 'As Quoted',
      savings: '10%+'
    }
  }));

  // Filter stories based on current filters
  useEffect(() => {
    let filtered = [...successStories];

    if (filters.projectType !== 'all') {
      filtered = filtered.filter(story => 
        story.projectType.toLowerCase() === filters.projectType.toLowerCase()
      );
    }

    // Sort stories
    switch (filters.sortBy) {
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      default:
        // Keep original order for 'recent'
        break;
    }

    setFilteredStories(filtered);
  }, [filters, projects]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      projectType: 'all',
      budgetRange: 'all',
      serviceCategory: 'all',
      sortBy: 'recent'
    });
  };

  const handleViewDetails = (story) => {
    setSelectedStory(story);
  };

  const handleCloseDetails = () => {
    setSelectedStory(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Success Stories - Project Transformations | Nirmitee Associates</title>
        <meta name="description" content="Discover how Nirmitee Associates has transformed construction projects across Pune. Real case studies showing cost savings, timeline reductions, and approval success stories." />
      </Helmet>

      <Header />

      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-16 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
                <Icon name="Trophy" size={16} />
                <span>Real Transformations, Real Results</span>
              </div>
              
              <h1 className="font-headline font-bold text-4xl lg:text-6xl text-foreground mb-6">
                Success Stories That
                <span className="text-primary block">Transform Projects</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Discover how Nirmitee Associates has helped hundreds of clients navigate complex construction challenges, save costs, and achieve their dream projects faster than ever imagined.
              </p>
            </div>
          </div>
        </section>

        {/* Stats Overview */}
        <section className="py-16 lg:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <StatsOverview stats={statsData} />
          </div>
        </section>

        {/* Featured Story */}
        {featuredStory && (
          <section className="py-16 lg:py-20 bg-muted/30">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <FeaturedStory 
                story={featuredStory} 
                onViewDetails={handleViewDetails}
              />
            </div>
          </section>
        )}

        {/* Filter and Stories Grid */}
        <section className="py-16 lg:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <FilterBar
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              resultCount={filteredStories.length}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredStories.map((story) => (
                <StoryCard
                  key={story.id}
                  story={story}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>

            {filteredStories.length === 0 && (
              <div className="text-center py-16">
                <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="font-headline font-bold text-xl text-foreground mb-2">
                  No Stories Found
                </h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your filters to see more success stories.
                </p>
                <Button
                  variant="outline"
                  iconName="RotateCcw"
                  iconPosition="left"
                  onClick={handleClearFilters}
                >
                  Clear All Filters
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Testimonials */}
        {mappedTestimonials.length > 0 && (
          <section className="py-16 lg:py-20 bg-muted/30">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <TestimonialSection testimonials={mappedTestimonials} />
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-16 lg:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <CTASection />
          </div>
        </section>
      </main>

      {/* Story Detail Modal */}
      {selectedStory && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-card rounded-lg construction-shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
              <h2 className="font-headline font-bold text-2xl text-card-foreground">
                {selectedStory.title}
              </h2>
              <button
                onClick={handleCloseDetails}
                className="w-10 h-10 bg-muted hover:bg-muted/80 rounded-full flex items-center justify-center transition-construction"
              >
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid lg:grid-cols-2 gap-8">
                <div>
                  <img
                    src={selectedStory.heroImage}
                    alt={selectedStory.title}
                    className="w-full h-64 object-cover rounded-lg mb-6"
                  />
                  
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-bold text-lg mb-2">Project Overview</h3>
                      <p className="text-muted-foreground">{selectedStory.description}</p>
                    </div>
                    
                    {selectedStory.servicesUsed && selectedStory.servicesUsed.length > 0 && (
                      <div>
                        <h3 className="font-bold text-lg mb-2">Services Utilized</h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedStory.servicesUsed.map((service, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full"
                            >
                              {service}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-4 bg-success/10 rounded-lg">
                      <p className="text-2xl font-bold text-success">{selectedStory.timeReduction}</p>
                      <p className="text-sm text-muted-foreground">Timeline</p>
                    </div>
                    <div className="text-center p-4 bg-primary/10 rounded-lg">
                      <p className="text-2xl font-bold text-primary">{selectedStory.costSavings}</p>
                      <p className="text-sm text-muted-foreground">Savings</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-bold text-lg mb-2">Client Information</h3>
                      <div className="flex items-center space-x-3">
                        <img
                          src={selectedStory.client.avatar}
                          alt={selectedStory.client.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-medium">{selectedStory.client.name}</p>
                          <p className="text-sm text-muted-foreground">{selectedStory.client.location}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-bold text-lg mb-2">Project Results</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Budget:</span>
                          <span className="font-medium">{selectedStory.results.budget}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Rating:</span>
                          <div className="flex items-center space-x-1">
                            {[...Array(5)].map((_, i) => (
                              <Icon
                                key={i}
                                name="Star"
                                size={16}
                                className={i < selectedStory.rating ? 'text-warning fill-current' : 'text-muted-foreground/30'}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default SuccessStoriesPage;